import { useCallback, useMemo } from 'react';
import {
  declareUnavailable,
  declaredAbsentInstancesAt,
  restoreAvailable,
  sunkAt,
  unavailableAt,
  type LocationUnavailable,
  type LocationUnavailableEntry,
} from '@heraion/shared';

import { getInstanceSurveyForSlug } from './useEntryHistory';
import { useProfileListOverlay } from './_useProfileListOverlay';

// EQ-1-GYM-COLDSTART — the swap picker's half of the declared-absence overlay.
//
// ⛔ It DECLARES, it never derives (equipment-model.md §11.6, Antenore
// 2026-08-17). "X is here" comes out of the log; "Y is NOT here" only ever
// comes out of the ✕ dialog. Nothing in this file reads a swap, a gap or any
// other ambiguous signal — a swap happens because the machine was busy, the
// knee hurt or the programme said so, and reading absence out of it would mint
// a fact nobody stated.
//
// The rules themselves live in @heraion/shared/locationUnavailable, shared
// with the API and testable without a renderer. What is here is what only a
// screen knows: which answer is being shown right now, and when to write.

/** What came back from the dialog: the athlete's WHOLE answer for one pair. */
export type UnavailableAnswer = {
  /** Named machine values (`machine-a`) ticked as missing. */
  instances: string[];
  /** The virtual Default (EQ-1-DEFAULT), which has no slug to store. */
  unnamed: boolean;
};

export type LocationUnavailableControl = {
  /**
   * Where the declaration would be keyed, or null when nothing can be.
   *
   * ⚠️ Null disables the whole gesture. Until `EQ-1-PLACEID` the place is free
   * text, so the key IS the slug the athlete typed; with no place there is no
   * key at all, and a "not here" with no "here" is not storable. The ✕ is
   * omitted rather than shown-and-inert.
   */
  place: string | null;
  /** The overlay as the screen currently shows it (optimistic while writing). */
  list: LocationUnavailable | undefined;
  /** What is already declared for one exercise here — the dialog's pre-tick. */
  declaredFor: (exercise: string) => LocationUnavailableEntry | undefined;
  /**
   * R6 — the named machines declared absent here, per exercise.
   *
   * ⛔ Feeds what the machine tray OFFERS, not what the list SHOWS. A
   * declared-absent exercise still sinks-and-stays; a declared-absent machine
   * is withheld, because picking one stamps an identity on the set.
   */
  absentInstances: (exercise: string) => readonly string[];
  /**
   * Exercises with NOTHING left to train them on here — the ones that sink.
   *
   * ⛔ Not "has a declaration": one machine of two declared missing must not
   * sink an exercise the athlete can still do. The predicate is
   * `hasAvailableInstance`, unchanged and un-reimplemented.
   */
  sunk: ReadonlySet<string>;
  declare: (exercise: string, answer: UnavailableAnswer) => void;
  restore: (exercise: string) => void;
};

export function useLocationUnavailable({
  visible,
  place,
  declared,
  patchProfile,
  notify,
  t,
}: {
  /** The picker's own visibility — closing it drops the optimistic overlay. */
  visible: boolean;
  place: string | null;
  /** `Profile.locationUnavailable` as the host last read it. */
  declared: LocationUnavailable | undefined;
  /** The existing door (hostDeps.patchProfile), narrowed to this one field —
   *  same shape useRestPicker takes for `defaultRestSec`. No new queue. */
  patchProfile: (patch: {
    locationUnavailable?: LocationUnavailable;
  }) => Promise<void>;
  notify: (title: string, body: string) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): LocationUnavailableControl {
  // Mechanics shared with the ★'s hook (_useProfileListOverlay): optimistic
  // write, roll back on failure, empty list sent as ABSENT so the API's
  // setIfPresent removes the attribute instead of storing a stale array.
  //
  // ⭐ Optimistic and not cleared on success, which is the property that made it
  // worth sharing: the effect of the ✕ is that the exercise sinks in a list the
  // athlete is still looking at, so waiting for a refetch would read as the tap
  // having been ignored. The ★ has the same need for the same reason.
  const { list, write } = useProfileListOverlay({
    visible,
    place,
    source: declared,
    field: 'locationUnavailable' as const,
    patchProfile,
    notify,
    errorTitle: () =>
      t('swap.unavailableErrorTitle', { defaultValue: 'Not saved' }),
    errorBody: () =>
      t('swap.unavailableErrorBody', {
        defaultValue: 'That gear is still marked as available here.',
      }),
  });

  const declaredFor = useCallback(
    (exercise: string): LocationUnavailableEntry | undefined =>
      place === null ? undefined : unavailableAt(list, place, exercise),
    [list, place],
  );

  const absentInstances = useCallback(
    (exercise: string): readonly string[] =>
      declaredAbsentInstancesAt(list, place, exercise),
    [list, place],
  );

  const sunk = useMemo<ReadonlySet<string>>(
    () =>
      sunkAt(list, place, (exercise) =>
        getInstanceSurveyForSlug(exercise, place ?? undefined),
      ),
    [list, place],
  );

  const declare = useCallback(
    (exercise: string, answer: UnavailableAnswer) => {
      if (place === null) return;
      write(
        declareUnavailable(list, {
          place,
          exercise,
          instances: answer.instances,
          unnamed: answer.unnamed,
          at: new Date().toISOString(),
        }),
      );
    },
    [list, place, write],
  );

  const restore = useCallback(
    (exercise: string) => {
      if (place === null) return;
      write(restoreAvailable(list, place, exercise));
    },
    [list, place, write],
  );

  return { place, list, declaredFor, absentInstances, sunk, declare, restore };
}
